import { FC, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { Box, Button, Typography } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { RegisterUserRequest } from "@/store/api/authApiData";
import { useLazyValidateRegistrationCodeQuery } from "@/store/api/authApi";
import { styles } from "../stylesSignUp";
import CustomInput from "@/components/UI/CustomInput/CustomInput";
import HeaderControl from "./HeaderControl";
import TitleBlock from "./TitleBlock";
import BottomLink from "./BottomLink";

type FormInput = {
  registrationCode: string;
};

interface SignUpInviteCodeProps {
  setSignUpStep: React.Dispatch<React.SetStateAction<number>>;
  setSignUpData: (data: Partial<RegisterUserRequest>) => void;
  signUpData: RegisterUserRequest;
}

const SignUpInviteCode: FC<SignUpInviteCodeProps> = ({
  setSignUpStep,
  setSignUpData,
  signUpData,
}) => {
  const { register, handleSubmit, watch } = useForm<FormInput>({
    mode: "all",
    defaultValues: { registrationCode: signUpData.registrationCode ?? "" },
  });

  const navigate = useNavigate();
  const codeValue = watch("registrationCode");

  const [validateCode, { isFetching }] = useLazyValidateRegistrationCodeQuery();
  const [codeError, setCodeError] = useState(false);

  useEffect(() => {
    if (codeError) setCodeError(false);
  }, [codeValue]);

  const onSubmit = async (data: FormInput) => {
    const code = data.registrationCode.trim();
    if (!code) return;

    try {
      const { valid } = await validateCode(code).unwrap();
      if (!valid) {
        setCodeError(true);
        return;
      }
      setSignUpData({ ...signUpData, registrationCode: code });
      setSignUpStep(1);
    } catch {
      setCodeError(true);
    }
  };

  return (
    <Box sx={styles.wrapper}>
      <HeaderControl onClick={() => navigate("/login")} logo />

      <Box sx={{ marginTop: "32px" }}>
        <TitleBlock title={"Sign up"} subtitle="Enter your invite code to get started." />

        <Box
          component="form"
          onSubmit={handleSubmit(onSubmit)}
          sx={{ width: "100%", marginTop: "44px" }}
        >
          <Box sx={{ marginBottom: "42px" }}>
            <CustomInput
              label="Invite code"
              placeholder="e.g 12345678"
              register={register}
              name="registrationCode"
              value={codeValue}
              error={codeError}
              helperText={codeError ? "Invalid code. Please check and try again." : null}
            />
          </Box>

          <Typography sx={styles.waitlistText}>
            No Code?{" "}
            <Box component="span" onClick={() => setSignUpStep(5)} sx={styles.waitlistLink}>
              Join Waitlist
            </Box>
          </Typography>

          <Button
            type="submit"
            fullWidth
            variant="contained"
            disabled={!codeValue?.trim() || isFetching}
            sx={styles.inviteButton}
          >
            Continue
          </Button>

          <BottomLink sx={{ mt: 4 }} />
        </Box>
      </Box>
    </Box>
  );
};

export default SignUpInviteCode;
